/** Utah Gas Corp aircraft; every leg uses this tail (CSV `aircraft` column is ignored). */
export const DEFAULT_AIRCRAFT_ID = "N426DH";

/** Jet-A density default for lbs ↔ gal on the manual form (matches worksheet rate default). */
export const DEFAULT_FUEL_LBS_PER_GALLON = 6.5;

export interface Rates {
  domesticSegmentUsdPerPassengerPerSegment: number;
  internationalFacilityUsdPerPerson: number;
  airTransportationPercentage: number;
  alaskaHawaiiDomesticDepartureUsdPerPerson: number;
  fuelLbsPerGallon: number;
}

export interface FlightLeg {
  /** Supabase row id when loaded or after save. */
  id?: string;
  flightDate: string;
  fromIcao: string;
  toIcao: string;
  passengerCount: number;
  segmentsDomestic: number;
  passengersDomesticSegmentTax: number | null;
  /**
   * CSV / form value for IRS No. 27 headcount. Tax math uses it only on **US ↔ non-US** legs
   * (`effectiveInternationalFacilityPassengers`); foreign–foreign and US–US ignore it.
   */
  internationalFacilityPassengers: number;
  alaskaHawaiiDomesticDeparturePassengers: number;
  charterOrFareAmountUsd: number;
  ruralAirportExempt: boolean;
  /** If true, this leg is personal (non-business); air excise allocable to it is shown as reimburse Utah Gas Corp. */
  personalUse: boolean;
  excludeFrom720: boolean;
  fuelLbs: number | null;
  fuelGallonsReported: number | null;
  /** $/gal; with gallons yields fuel cost and 2× personal reimbursement. */
  fuelPricePerGallon?: number | null;
  aircraftId: string;
  routeNotes: string;
  /**
   * Free-text names (<code>;</code>-separated) for log / display. Domestic segment excise uses
   * <code>passengerCount</code> and optional <code>passengersDomesticSegmentTax</code> (<code>dom_pax</code>).
   */
  passengerList?: string | null;
  /**
   * Optional crew roster (<code>;</code>-separated) for log / display; not used for domestic segment headcount.
   */
  crewList?: string | null;
  tachStart?: number | null;
  tachEnd?: number | null;
  /** Landing(s) on this leg occurred in Texas (count = leg landings, default 1 if blank). */
  texasArrival?: boolean;
  /** Hobbs/tach hours for variable program charge (e.g. Honda $/hr). */
  flightHours: number | null;
  /** Landings this leg (program per-landing charge). Empty in form → treated as 1 on Utah Gas Corp tab rollups. */
  landings: number | null;
  /** Takeoffs this leg; empty → 1. Used with departure state for Texas takeoff counts. */
  takeoffs: number | null;
  /** 2-letter US departure state (e.g. TX). If TX, takeoffs count toward “Texas takeoffs”. */
  departureState: string | null;
}

export interface QuarterlyTotals {
  year: number;
  quarter: number;
  irs26PercentageTax: number;
  irs26DomesticSegmentTax: number;
  irs27InternationalFacilityTax: number;
  /**
   * Section 4261 Alaska/Hawaii departure amount (reduced per-person rate). For Form 720 Part I,
   * IRS instructions discuss this under **IRS No. 27** with international — PDF export maps it to line 27.
   */
  irs26AkHiDepartureTax: number;
  fuelGallonsFromLog: number;
  legCount: number;
  /** Sum of IRS 26+27 air excise on legs marked personal (for reimbursement to Utah Gas Corp). */
  personalReimbursementAirExcise: number;
}

/**
 * One calendar month within a quarter — same line items as {@link QuarterlyTotals}, for Form 720
 * Part II–style “record of liability” (worksheet allocates by <strong>flight date</strong> month).
 */
export interface MonthlyForm720Totals {
  calendarYear: number;
  calendarMonth: number;
  irs26PercentageTax: number;
  irs26DomesticSegmentTax: number;
  irs27InternationalFacilityTax: number;
  /** See {@link QuarterlyTotals.irs26AkHiDepartureTax}. */
  irs26AkHiDepartureTax: number;
  fuelGallonsFromLog: number;
  legCount: number;
  personalReimbursementAirExcise: number;
}

type ExciseSubtotalFields = Pick<
  QuarterlyTotals,
  | "irs26PercentageTax"
  | "irs26DomesticSegmentTax"
  | "irs26AkHiDepartureTax"
>;

export function irs26Total(t: ExciseSubtotalFields): number {
  return (
    t.irs26PercentageTax + t.irs26DomesticSegmentTax + t.irs26AkHiDepartureTax
  );
}

type AirExciseTotalFields = ExciseSubtotalFields &
  Pick<QuarterlyTotals, "irs27InternationalFacilityTax">;

export function totalExciseAir(t: AirExciseTotalFields): number {
  return irs26Total(t) + t.irs27InternationalFacilityTax;
}
